import React, { useState, useEffect, useMemo, useRef } from 'react';

export function LedgerDropdown({ value, onChange, ledgers, suggested, memoryLedger, disabled }: any) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [highlighted, setHighlighted] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    if (!q) return ledgers;
    return ledgers.filter((l: string) => l.toLowerCase().includes(q));
  }, [search, ledgers]);

  const sortedFiltered = useMemo(() => {
    const priority = new Set([memoryLedger, suggested].filter(Boolean));
    return [
      ...filtered.filter((l: string) => priority.has(l)),
      ...filtered.filter((l: string) => !priority.has(l)),
    ];
  }, [filtered, memoryLedger, suggested]);

  const handleSelect = (ledger: string) => {
    onChange(ledger);
    setOpen(false);
    requestAnimationFrame(() => setSearch(''));
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!open) { if (e.key === 'ArrowDown' || e.key === 'Enter') setOpen(true); return; }
    if (e.key === 'ArrowDown') { e.preventDefault(); setHighlighted(h => Math.min(h + 1, sortedFiltered.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setHighlighted(h => Math.max(h - 1, 0)); }
    else if (e.key === 'Enter') { e.preventDefault(); if (sortedFiltered[highlighted]) handleSelect(sortedFiltered[highlighted]); }
    else if (e.key === 'Escape') { setOpen(false); setSearch(''); }
  };

  useEffect(() => {
    if (highlighted >= sortedFiltered.length) setHighlighted(0);
  }, [sortedFiltered.length, highlighted]);

  useEffect(() => {
    if (open && menuRef.current) {
      const el = menuRef.current.children[highlighted] as HTMLElement;
      if (el) el.scrollIntoView({ block: 'nearest' });
    }
  }, [highlighted, open]);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (
        !menuRef.current?.contains(e.target as Node) &&
        !inputRef.current?.contains(e.target as Node)
      ) { setOpen(false); }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <div className="ledger-dropdown-wrap">
      <input
        ref={inputRef}
        className={`ledger-input${!value ? ' missing' : ' has-value'}`}
        value={open ? search : (value ?? '')}
        placeholder={value || 'Search ledger...'}
        disabled={disabled}
        onFocus={() => { setOpen(true); setSearch(''); setHighlighted(0); }}
        onChange={e => { setSearch(e.target.value); setHighlighted(0); }}
        onKeyDown={handleKeyDown}
        autoComplete="off"
      />
      {open && (
        <div className="ledger-dropdown-menu" ref={menuRef}>
          {sortedFiltered.length === 0
            ? <div className="ledger-dropdown-empty">No ledger found</div>
            : sortedFiltered.map((l: string, i: number) => {
              const isMem = l === memoryLedger;
              const isSug = l === suggested && !isMem;
              return (
                <div
                  key={l}
                  className={`ledger-dropdown-item${i === highlighted ? ' highlighted' : ''}${isMem || isSug ? ' suggested' : ''}`}
                  onMouseDown={() => handleSelect(l)}
                  onMouseEnter={() => setHighlighted(i)}
                  title={isMem ? 'Previously used for this party' : isSug ? 'Suggested based on party name' : ''}
                >
                  {l}
                  {isMem && <span style={{ fontSize: 10, color: 'var(--accent)', marginLeft: 6 }}>↩ last used</span>}
                  {isSug && <span style={{ fontSize: 10, color: 'var(--amber)', marginLeft: 6 }}>★ suggested</span>}
                </div>
              );
            })
          }
        </div>
      )}
      <div className={`ledger-sub ${!value ? 'missing' : 'ready'}`}>
        {!value ? 'Requires Ledger' : '✓ Ready'}
      </div>
    </div>
  );
}
